/**
 * ExportScreen
 * Export user data to CSV or JSON format
 */

import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  ScrollView,
  ActivityIndicator,
  Alert,
  Platform,
} from 'react-native';
import { ExportScreenProps } from '../navigation/types';
import { exportToCSV, generateCSVFilename } from '../services/export/CSVExporter';
import { exportToJSON, generateJSONFilename } from '../services/export/JSONExporter';

export const ExportScreen: React.FC<ExportScreenProps> = ({ navigation }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportType, setExportType] = useState<'csv' | 'json' | null>(null);

  const downloadFile = (content: string, filename: string, mimeType: string) => {
    if (Platform.OS === 'web') {
      // Web: trigger download using blob
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      // Native: would use react-native-fs or share API
      // For now, just show an alert
      Alert.alert(
        'Export Complete',
        `Data exported to ${filename}. On native platforms, this would save to your device.`,
        [{ text: 'OK' }]
      );
    }
  };

  const handleExportCSV = async () => {
    try {
      setIsExporting(true);
      setExportType('csv');

      const csvContent = await exportToCSV();
      const filename = generateCSVFilename();

      downloadFile(csvContent, filename, 'text/csv');

      Alert.alert(
        'Export Successful',
        `Your data has been exported to ${filename}`,
        [{ text: 'OK' }]
      );
    } catch (error) {
      Alert.alert('Export Failed', error instanceof Error ? error.message : 'Failed to export data');
    } finally {
      setIsExporting(false);
      setExportType(null);
    }
  };

  const handleExportJSON = async () => {
    try {
      setIsExporting(true);
      setExportType('json');

      const jsonContent = await exportToJSON();
      const filename = generateJSONFilename();

      downloadFile(jsonContent, filename, 'application/json');

      Alert.alert(
        'Export Successful',
        `Your data has been exported to ${filename}`,
        [{ text: 'OK' }]
      );
    } catch (error) {
      Alert.alert('Export Failed', error instanceof Error ? error.message : 'Failed to export data');
    } finally {
      setIsExporting(false);
      setExportType(null);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <View style={styles.content}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title}>Export Your Data</Text>
            <Text style={styles.subtitle}>
              Download all your goals and completion history. Your data belongs to you.
            </Text>
          </View>

          {/* Export Options */}
          <View style={styles.optionsContainer}>
            {/* CSV Export */}
            <TouchableOpacity
              style={[styles.exportCard, isExporting && exportType !== 'csv' && styles.exportCardDisabled]}
              onPress={handleExportCSV}
              disabled={isExporting}
            >
              <View style={styles.exportCardHeader}>
                <Text style={styles.exportCardIcon}>📊</Text>
                <View style={styles.exportCardTitleContainer}>
                  <Text style={styles.exportCardTitle}>CSV Format</Text>
                  <Text style={styles.exportCardSubtitle}>Spreadsheet-friendly</Text>
                </View>
              </View>
              <Text style={styles.exportCardDescription}>
                Export as CSV (Comma-Separated Values) for use in Excel, Google Sheets, or other
                spreadsheet applications.
              </Text>
              {isExporting && exportType === 'csv' ? (
                <View style={styles.exportingContainer}>
                  <ActivityIndicator color="#007AFF" />
                  <Text style={styles.exportingText}>Exporting...</Text>
                </View>
              ) : (
                <View style={styles.exportButton}>
                  <Text style={styles.exportButtonText}>Export as CSV</Text>
                </View>
              )}
            </TouchableOpacity>

            {/* JSON Export */}
            <TouchableOpacity
              style={[styles.exportCard, isExporting && exportType !== 'json' && styles.exportCardDisabled]}
              onPress={handleExportJSON}
              disabled={isExporting}
            >
              <View style={styles.exportCardHeader}>
                <Text style={styles.exportCardIcon}>📄</Text>
                <View style={styles.exportCardTitleContainer}>
                  <Text style={styles.exportCardTitle}>JSON Format</Text>
                  <Text style={styles.exportCardSubtitle}>Complete data backup</Text>
                </View>
              </View>
              <Text style={styles.exportCardDescription}>
                Export as JSON with complete event log. Includes all data for backup and
                programmatic access.
              </Text>
              {isExporting && exportType === 'json' ? (
                <View style={styles.exportingContainer}>
                  <ActivityIndicator color="#007AFF" />
                  <Text style={styles.exportingText}>Exporting...</Text>
                </View>
              ) : (
                <View style={styles.exportButton}>
                  <Text style={styles.exportButtonText}>Export as JSON</Text>
                </View>
              )}
            </TouchableOpacity>
          </View>

          {/* Info Section */}
          <View style={styles.infoSection}>
            <Text style={styles.infoTitle}>What's Included?</Text>
            <View style={styles.infoList}>
              <Text style={styles.infoItem}>• All your accounts and goals</Text>
              <Text style={styles.infoItem}>• Complete completion history</Text>
              <Text style={styles.infoItem}>• Timestamps and metadata</Text>
              <Text style={styles.infoItem}>• Full event log (JSON only)</Text>
            </View>
            <Text style={styles.infoNote}>
              Your data is exported directly from your device. No data is sent to external servers.
            </Text>
          </View>

          {/* Back Button */}
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
            disabled={isExporting}
          >
            <Text style={styles.backButtonText}>Back to Settings</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  content: {
    flex: 1,
    padding: 24,
  },
  header: {
    marginBottom: 32,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    color: '#333',
    marginBottom: 12,
  },
  subtitle: {
    fontSize: 16,
    color: '#666',
    lineHeight: 24,
  },
  optionsContainer: {
    marginBottom: 32,
  },
  exportCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    borderWidth: 2,
    borderColor: '#e0e0e0',
  },
  exportCardDisabled: {
    opacity: 0.5,
  },
  exportCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  exportCardIcon: {
    fontSize: 32,
    marginRight: 12,
  },
  exportCardTitleContainer: {
    flex: 1,
  },
  exportCardTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#333',
    marginBottom: 2,
  },
  exportCardSubtitle: {
    fontSize: 13,
    color: '#999',
  },
  exportCardDescription: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
    marginBottom: 16,
  },
  exportButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  exportButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  exportingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
  },
  exportingText: {
    marginLeft: 12,
    fontSize: 16,
    color: '#007AFF',
    fontWeight: '600',
  },
  infoSection: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 24,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  infoTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 12,
  },
  infoList: {
    marginBottom: 16,
  },
  infoItem: {
    fontSize: 14,
    color: '#666',
    lineHeight: 24,
  },
  infoNote: {
    fontSize: 13,
    color: '#999',
    fontStyle: 'italic',
    lineHeight: 18,
  },
  backButton: {
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderWidth: 1,
    borderColor: '#ddd',
  },
  backButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#666',
  },
});
